/**
 * Replo Accessibility Fixer
 * 
 * Automatically fixes common accessibility issues in Replo-generated HTML.
 * This script can be injected into Replo pages via Custom Code blocks.
 * 
 * Usage in Replo: Add this script in a Custom Code block at the bottom of your page
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runFixes);
    } else {
      runFixes();
    }
  }

  function runFixes() {
    // Run fixes after a short delay to ensure Replo content is loaded
    setTimeout(function() {
      fixImages();
      fixButtons();
      fixLinks();
      fixPrices();
      fixFormInputs();
      fixHeadings();
      fixStrikePrices();
    }, 500);

    // Also run on dynamic content changes (Replo carousels, etc.)
    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(function(mutations) {
        var shouldRefix = false;
        mutations.forEach(function(mutation) {
          if (mutation.addedNodes.length > 0) {
            shouldRefix = true;
          }
        });
        if (shouldRefix) {
          setTimeout(function() {
            fixImages();
            fixButtons();
            fixLinks();
            fixPrices();
            fixStrikePrices();
          }, 300);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Fix images without alt text
   */
  function fixImages() {
    var images = document.querySelectorAll('#replo-fullpage-element img:not([alt])');
    images.forEach(function(img) {
      // Try to infer alt text from nearby text or parent
      var altText = '';
      var parent = img.parentElement;
      
      // Check for nearby heading or text
      var heading = parent ? parent.querySelector('h1, h2, h3, h4, h5, h6') : null;
      if (heading) {
        altText = heading.textContent.trim();
      } else {
        // Check for aria-label on parent
        var parentAriaLabel = parent ? parent.getAttribute('aria-label') : null;
        if (parentAriaLabel) {
          altText = parentAriaLabel;
        } else {
          // Fallback: use image filename or generic description
          var src = img.src || '';
          var filename = src.split('/').pop().split('?')[0];
          if (filename && filename !== '') {
            altText = filename.replace(/[-_]/g, ' ').replace(/\.[^.]+$/, '');
          } else {
            altText = 'Image';
          }
        }
      }
      
      if (altText) {
        img.setAttribute('alt', altText);
      }
    });
  }

  /**
   * Fix buttons without accessible names
   */
  function fixButtons() {
    var buttons = document.querySelectorAll('#replo-fullpage-element button:not([aria-label]):not([aria-labelledby])');
    buttons.forEach(function(btn) {
      var text = btn.textContent.trim();
      var ariaLabel = null;
      
      // Check for SVG icons (common in Replo carousels)
      var svg = btn.querySelector('svg');
      if (svg && !text) {
        // Check for aria-label on SVG
        var svgLabel = svg.getAttribute('aria-label');
        if (svgLabel) {
          ariaLabel = svgLabel;
        } else {
          // Try to infer from SVG path or role
          var role = svg.getAttribute('role');
          if (role === 'presentation') {
            // Check surrounding context
            var parent = btn.parentElement;
            var context = parent ? parent.getAttribute('aria-label') : null;
            if (context) {
              ariaLabel = context;
            } else {
              // Generic based on button position or class
              var classes = btn.className || '';
              if (classes.includes('prev') || classes.includes('left') || classes.includes('back')) {
                ariaLabel = 'Previous';
              } else if (classes.includes('next') || classes.includes('right') || classes.includes('forward')) {
                ariaLabel = 'Next';
              } else if (classes.includes('close')) {
                ariaLabel = 'Close';
              } else {
                ariaLabel = 'Button';
              }
            }
          }
        }
      } else if (text) {
        ariaLabel = text;
      } else {
        ariaLabel = 'Button';
      }
      
      if (ariaLabel) {
        btn.setAttribute('aria-label', ariaLabel);
      }
    });
  }

  /**
   * Fix links without accessible names
   */
  function fixLinks() {
    var links = document.querySelectorAll('#replo-fullpage-element a:not([aria-label]):not([aria-labelledby])');
    links.forEach(function(link) {
      var text = link.textContent.trim();
      var ariaLabel = null;
      
      // Check for images inside link
      var img = link.querySelector('img');
      if (img && img.alt) {
        ariaLabel = img.alt;
      } else if (text) {
        ariaLabel = text;
      } else {
        // Try to get from title attribute
        var title = link.getAttribute('title');
        if (title) {
          ariaLabel = title;
        } else {
          // Fallback: use href
          var href = link.getAttribute('href');
          if (href && href !== '#') {
            ariaLabel = 'Link to ' + href.split('/').pop().replace(/[-_]/g, ' ');
          } else {
            ariaLabel = 'Link';
          }
        }
      }
      
      if (ariaLabel) {
        link.setAttribute('aria-label', ariaLabel);
      }
    });
  }

  /**
   * Find all price elements containing USD prices ($)
   * Returns array of elements with their text content and computed styles
   */
  function findAllPrices() {
    var reploSection = document.getElementById('replo-fullpage-element');
    if (!reploSection) return [];
    
    var allElements = reploSection.querySelectorAll('*');
    var priceElements = [];
    var processedElements = new Set(); // Track processed elements to avoid duplicates
    
    allElements.forEach(function(el) {
      // Skip if already processed
      if (processedElements.has(el)) return;
      
      // Get text content (this will include nested text)
      var text = el.textContent.trim();
      
      // Check if element contains ONLY a price (starts with $, matches price pattern)
      // Match: $25.58, $76.94, $1,234.56, etc.
      var priceMatch = text.match(/^\$[\d,]+(\.\d{2})?$/);
      
      if (priceMatch) {
        // Check if this element or any parent has strikethrough styling
        var hasStrikethrough = false;
        var checkEl = el;
        
        while (checkEl && checkEl !== reploSection && !hasStrikethrough) {
          var style = window.getComputedStyle(checkEl);
          
          // Check both textDecoration and textDecorationLine (for browser compatibility)
          if (style.textDecoration && style.textDecoration.includes('line-through')) {
            hasStrikethrough = true;
          } else if (style.textDecorationLine && style.textDecorationLine.includes('line-through')) {
            hasStrikethrough = true;
          }
          
          checkEl = checkEl.parentElement;
        }
        
        // Mark this element and its children as processed
        processedElements.add(el);
        var children = el.querySelectorAll('*');
        children.forEach(function(child) {
          processedElements.add(child);
        });
        
        priceElements.push({
          element: el,
          text: text,
          isStrikethrough: hasStrikethrough,
          price: parseFloat(text.replace(/[$,]/g, ''))
        });
      }
    });
    
    return priceElements;
  }

  /**
   * Fix pricing elements - ensure proper aria-labels and accessible structure
   */
  function fixPrices() {
    var prices = findAllPrices();
    
    prices.forEach(function(priceData) {
      var el = priceData.element;
      var isStrikethrough = priceData.isStrikethrough;
      
      // Skip if already processed
      if (el.closest('[aria-label="Original price"]') || 
          el.closest('[aria-label="Discounted price"]') ||
          el.closest('[aria-label="Price"]')) {
        return;
      }
      
      if (isStrikethrough) {
        // This is a strikethrough (original) price - apply accessible structure
        applyAccessibleStrikePrice(el, priceData.text);
      } else {
        // This is a regular price - check if there's a strikethrough price nearby
        var hasStrikethroughNearby = false;
        var parent = el.parentElement;
        
        // Check siblings and parent for strikethrough prices
        if (parent) {
          var siblings = Array.from(parent.children);
          siblings.forEach(function(sibling) {
            if (sibling !== el) {
              var siblingText = sibling.textContent.trim();
              if (siblingText && siblingText.match(/^\$[\d,]+(\.\d{2})?$/)) {
                var siblingStyle = window.getComputedStyle(sibling);
                if (siblingStyle.textDecoration.includes('line-through') || 
                    siblingStyle.textDecorationLine.includes('line-through')) {
                  hasStrikethroughNearby = true;
                }
              }
            }
          });
        }
        
        // Set appropriate aria-label
        var ariaLabel = hasStrikethroughNearby ? 'Discounted price' : 'Price';
        el.setAttribute('aria-label', ariaLabel);
      }
    });
  }

  /**
   * Apply accessible structure to a strikethrough price element
   */
  function applyAccessibleStrikePrice(element, priceText) {
    // Skip if already processed
    if (element.closest('[aria-label="Original price"]')) return;
    
    // Create wrapper span with aria-label
    var wrapper = document.createElement('span');
    wrapper.setAttribute('aria-label', 'Original price');
    
    // Preserve original element's attributes on wrapper
    Array.from(element.attributes).forEach(function(attr) {
      if (attr.name !== 'aria-label' && attr.name !== 'aria-hidden') {
        wrapper.setAttribute(attr.name, attr.value);
      }
    });
    
    // Clone the original element to preserve its structure, classes, and CSS styling
    // The CSS classes will handle the visual strikethrough
    var tagName = element.tagName.toLowerCase();
    var inner = null;
    
    if (tagName === 'div' || tagName === 'p') {
      // Convert block elements to span to maintain consistent structure
      inner = document.createElement('span');
    } else {
      inner = element.cloneNode(false);
    }
    
    inner.textContent = priceText;
    // Preserve all classes and styles from original element (including strikethrough CSS)
    if (element.className) {
      inner.className = element.className;
    }
    if (element.style && element.style.cssText) {
      inner.style.cssText = element.style.cssText;
    }
    
    // Build structure: wrapper > styled element (CSS handles strikethrough)
    // aria-label on wrapper provides screen reader announcement
    wrapper.appendChild(inner);
    
    // Replace the original element
    if (element.parentNode) {
      element.parentNode.replaceChild(wrapper, element);
    }
  }

  /**
   * Fix strike-down pricing to use accessible structure
   * This function now uses findAllPrices() to find strikethrough prices
   */
  function fixStrikePrices() {
    var prices = findAllPrices();
    
    prices.forEach(function(priceData) {
      if (priceData.isStrikethrough) {
        applyAccessibleStrikePrice(priceData.element, priceData.text);
      }
    });
  }

  /**
   * Fix form inputs without labels
   */
  function fixFormInputs() {
    var inputs = document.querySelectorAll('#replo-fullpage-element input:not([type="hidden"]):not([aria-label]):not([aria-labelledby])');
    inputs.forEach(function(input) {
      var type = input.type || 'text';
      var placeholder = input.placeholder || '';
      var name = input.name || '';
      var id = input.id || '';
      
      // Check if label exists
      var label = null;
      if (id) {
        label = document.querySelector('label[for="' + id + '"]');
      }
      if (!label && input.parentElement) {
        label = input.parentElement.querySelector('label');
      }
      
      if (!label) {
        // Create aria-label from available information
        var ariaLabel = placeholder || name || (type === 'email' ? 'Email address' : type === 'tel' ? 'Phone number' : 'Input');
        input.setAttribute('aria-label', ariaLabel);
        
        // Also create a visually hidden label if possible
        if (!input.id) {
          input.id = 'replo-input-' + Math.random().toString(36).substr(2, 9);
        }
        var hiddenLabel = document.createElement('label');
        hiddenLabel.className = 'sr-only';
        hiddenLabel.setAttribute('for', input.id);
        hiddenLabel.textContent = ariaLabel;
        input.parentNode.insertBefore(hiddenLabel, input);
      }
    });
  }

  /**
   * Fix heading hierarchy - ensure proper h1-h6 structure
   */
  function fixHeadings() {
    var reploSection = document.getElementById('replo-fullpage-element');
    if (!reploSection) return;
    
    var headings = reploSection.querySelectorAll('h1, h2, h3, h4, h5, h6');
    var lastLevel = 0;
    
    headings.forEach(function(heading) {
      var currentLevel = parseInt(heading.tagName.charAt(1));
      
      // If we skip levels (e.g., h1 to h3), fix it
      if (lastLevel > 0 && currentLevel > lastLevel + 1) {
        var fixedLevel = lastLevel + 1;
        var newHeading = document.createElement('h' + fixedLevel);
        newHeading.innerHTML = heading.innerHTML;
        newHeading.className = heading.className;
        newHeading.id = heading.id;
        Array.from(heading.attributes).forEach(function(attr) {
          if (attr.name !== 'tagName') {
            newHeading.setAttribute(attr.name, attr.value);
          }
        });
        heading.parentNode.replaceChild(newHeading, heading);
        lastLevel = fixedLevel;
      } else {
        lastLevel = currentLevel;
      }
    });
  }

  // Initialize
  init();
})();

